import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { optimizedImage } from "@/lib/image";

const BG_IMAGE = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/7htetnuv_photo%20boutique%20abd%20.jpeg";

const boutiquePhotos = [
  {
    id: 1,
    src: "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/accuzggl_photo%20boutique%20.jpeg",
    alt: "Hangar - mobilier ancien et brocante",
  },
  {
    id: 2,
    src: "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/ex6xoue7_photoo%20boutique%20.jpeg",
    alt: "Salon vintage - fauteuil orange et bibliothèque",
  },
  {
    id: 3,
    src: "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/hf2r2amg_photoo%20boutiquee.jpeg",
    alt: "Commode chinoise et téléphone vintage",
  },
  {
    id: 4,
    src: "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/0k8eksg1_photooo%20boutiquee.jpeg",
    alt: "Espace vitrines - verrerie, tableaux et objets d'art",
  },
];

const PhoneButton = ({ className, children }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className={className}>{children}</button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-white">
      <DropdownMenuItem asChild>
        <a href="tel:0608691129" className="flex items-center gap-2 cursor-pointer">
          <Phone className="w-4 h-4" /> Appeler
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <a href="sms:0608691129" className="flex items-center gap-2 cursor-pointer">
          <MessageCircle className="w-4 h-4" /> Envoyer un SMS
        </a>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const BoutiquePage = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  const next = () => setActive((i) => (i + 1) % boutiquePhotos.length);
  const prev = () => setActive((i) => (i - 1 + boutiquePhotos.length) % boutiquePhotos.length);

  return (
    <div className="min-h-screen bg-[#FAFAF9] relative">
      {/* Floating header */}
      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          <button
            onClick={() => navigate("/")}
            className="pointer-events-auto flex items-center gap-2 text-white bg-[#4A3728]/70 hover:bg-[#4A3728] backdrop-blur-sm px-4 py-2 rounded-full transition-colors"
            data-testid="btn-retour"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Retour</span>
          </button>

          <PhoneButton className="pointer-events-auto flex items-center gap-2 text-white bg-[#4A3728]/70 hover:bg-[#4A3728] backdrop-blur-sm px-4 py-2 rounded-full font-medium transition-colors">
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">06 08 69 11 29</span>
          </PhoneButton>
        </div>
      </div>

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={optimizedImage(BG_IMAGE, { w: 1920, q: 75 })}
            alt="Au Bon Débarras"
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#4A3728]/65 via-[#4A3728]/55 to-[#4A3728]/85" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-sm font-medium tracking-[0.3em] uppercase text-[#D4A373] mb-4 block">
              Boutique
            </span>
            <h1 className="font-serif text-5xl md:text-7xl text-white leading-tight">
              La Caverne d'AlibaBasile&nbsp;!
            </h1>
            <p className="mt-6 text-white/80 text-lg">
              102 Av. du Passous, 50230 Agon-Coutainville
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative">
            {/* Main image */}
            <div className="relative rounded-xl overflow-hidden shadow-2xl bg-stone-200 aspect-[4/3] md:aspect-[16/10]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={boutiquePhotos[active].id}
                  src={optimizedImage(boutiquePhotos[active].src, { w: 1600, q: 80 })}
                  alt={boutiquePhotos[active].alt}
                  className="w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  loading="lazy"
                  decoding="async"
                />
              </AnimatePresence>

              {/* Controls */}
              <button
                onClick={prev}
                aria-label="Photo précédente"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/85 hover:bg-white text-[#4A3728] flex items-center justify-center shadow-lg transition"
                data-testid="boutique-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={next}
                aria-label="Photo suivante"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/85 hover:bg-white text-[#4A3728] flex items-center justify-center shadow-lg transition"
                data-testid="boutique-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Counter */}
              <div className="absolute bottom-4 right-4 bg-[#4A3728]/85 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-sm font-medium">
                {active + 1} / {boutiquePhotos.length}
              </div>
            </div>

            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-3 md:gap-5 mt-5">
              {boutiquePhotos.map((photo, idx) => (
                <button
                  key={photo.id}
                  onClick={() => setActive(idx)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    idx === active ? "border-[#D97706] scale-[1.02] shadow-md" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`boutique-thumb-${idx}`}
                >
                  <img
                    src={optimizedImage(photo.src, { w: 320, q: 72 })}
                    alt={photo.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Infos */}
      <section className="py-16 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-stone-700 leading-relaxed text-lg max-w-3xl mx-auto mb-10">
            Décorations, bibelots, jouets anciens, vaisselles, mobiliers, objets d'art, luminaires, livres&nbsp;:
            la Caverne d'AlibaBasile regorge de trésors&nbsp;!
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <a
              href="https://www.google.com/maps/search/?api=1&query=102+Av.+du+Passous+50230+Agon-Coutainville"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#4A3728] hover:bg-[#3a2a1d] text-white rounded-full px-6 py-3 font-medium transition-all duration-300 hover:scale-105"
            >
              <MapPin className="w-5 h-5" />
              102 Av. du Passous, 50230 Agon-Coutainville
            </a>
            <PhoneButton className="inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#b96305] text-white rounded-full px-6 py-3 font-medium transition-all duration-300 hover:scale-105">
              <Phone className="w-5 h-5" />
              06 08 69 11 29
            </PhoneButton>
          </div>
        </div>
      </section>

      <div className="py-8 text-center bg-[#FAFAF9]">
        <p className="text-stone-500 text-sm">
          Au Bon Débarras | Boutique d'Agon-Coutainville
        </p>
        <p className="text-stone-400 text-sm mt-1 select-text">
          aubondebarras1@gmail.com
        </p>
      </div>
    </div>
  );
};

export default BoutiquePage;
